import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { subscribeToFriends, Friend } from '../firebase/friends';
import { subscribeToMultipleUserStatuses, UserStatus } from '../firebase/userStatus';

interface FriendsDropdownProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
}

const FriendsDropdown: React.FC<FriendsDropdownProps> = ({ userId, isOpen, onClose }) => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [statuses, setStatuses] = useState<{ [userId: string]: UserStatus }>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !isOpen) return;

    // Subscribe to friends
    const unsubscribeFriends = subscribeToFriends(userId, (friendsList) => {
      setFriends(friendsList);
      setLoading(false);

      // Subscribe to statuses for all friends
      const friendIds = friendsList.map(f => f.friendId);
      if (friendIds.length > 0) {
        const unsubscribe = subscribeToMultipleUserStatuses(friendIds, (updatedStatuses) => {
          setStatuses(updatedStatuses);
        });
        
        // Clean up on unmount
        return () => {
          unsubscribe();
        };
      }
    });

    return () => {
      unsubscribeFriends();
    };
  }, [userId, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="absolute top-12 right-0 w-[90vw] md:w-80 bg-dark-bg border border-dark-border rounded-lg shadow-lg max-h-96 overflow-hidden z-50">
      {/* Header */}
      <div className="p-4 border-b border-dark-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Friends
          </h3>
          <span className="text-xs text-text-muted">
            {friends.length} total
          </span>
        </div>
      </div>

      {/* Friends List */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-blue mx-auto mb-2"></div>
            <p className="text-text-muted text-sm">Loading friends...</p>
          </div>
        ) : friends.length === 0 ? (
          <div className="p-4 text-center text-text-muted">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No friends yet</p>
          </div>
        ) : (
          <>
            {/* Online Friends */}
            {friends.filter(f => statuses[f.friendId]?.isOnline).length > 0 && (
              <div className="p-2 border-b border-dark-border">
                <p className="text-xs text-text-muted uppercase font-semibold mb-2 px-2">
                  Online ({friends.filter(f => statuses[f.friendId]?.isOnline).length})
                </p>
                {friends
                  .filter(f => statuses[f.friendId]?.isOnline)
                  .map((friend) => {
                    const status = statuses[friend.friendId];
                    return (
                      <div
                        key={friend.id}
                        className="flex items-center px-3 py-2 hover:bg-dark-card rounded-lg transition-colors cursor-pointer"
                      >
                        {friend.friendImageUrl ? (
                          <div className="relative">
                            <img
                              src={friend.friendImageUrl}
                              alt={friend.friendName}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-bg"></div>
                          </div>
                        ) : (
                          <div className="relative w-10 h-10 rounded-full bg-accent-blue bg-opacity-20 flex items-center justify-center">
                            <Users className="w-5 h-5 text-accent-blue" />
                            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-bg"></div>
                          </div>
                        )}
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="text-sm font-medium text-text-primary truncate">
                            {friend.friendName}
                          </p>
                          <p className="text-xs text-text-muted">
                            {status?.status === 'online' ? 'Online' : 'Away'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Offline Friends */}
            <div className="p-2">
              <p className="text-xs text-text-muted uppercase font-semibold mb-2 px-2">
                Offline ({friends.filter(f => !statuses[f.friendId]?.isOnline).length})
              </p>
              {friends
                .filter(f => !statuses[f.friendId]?.isOnline)
                .map((friend) => (
                    <div
                      key={friend.id}
                      className="flex items-center px-3 py-2 hover:bg-dark-card rounded-lg transition-colors cursor-pointer opacity-60"
                    >
                      {friend.friendImageUrl ? (
                        <img
                          src={friend.friendImageUrl}
                          alt={friend.friendName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-accent-blue bg-opacity-20 flex items-center justify-center">
                          <Users className="w-5 h-5 text-accent-blue" />
                        </div>
                      )}
                      <div className="ml-3 flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {friend.friendName}
                        </p>
                        <p className="text-xs text-text-muted">
                          Offline
                        </p>
                      </div>
                    </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FriendsDropdown;

